import ActionTextIcon from "@/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteCalcDraft, useFilterCalcDrafts } from "../../../../common/api/queries";
import { QueryKeys, TableSizes } from "../../../../common/constants";
import { CalcType } from "../../enums";
import { CALC_ROUTE_PATHS } from "../../paths";
import { TravelCalcDraftList } from "../types";
import { CalcDraftAttachmentsTableView } from "./CalcDraftAttachmentsTableView";

export const TravelCalcDraftTableView = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const searchParam = useFilterSearchParams();

  const { data } = useFilterCalcDrafts({ ...searchParam, calcTypes: [CalcType.TRAVEL] });

  const deleteCalcDraft = useDeleteCalcDraft();

  const columns: ColumnsType<TravelCalcDraftList> = [
    {
      key: "clientName",
      title: t("calc.draft.attrs.clientName"),
      width: 220,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.clientName}</Ellipsis>,
    },
    {
      key: "insuranceType",
      title: t("calc.travel.enums.insuranceType._label"),
      width: 170,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{t("calc.travel.enums.insuranceType." + record.insuranceType)}</Ellipsis>,
    },
    {
      key: "destination",
      title: t("calc.travel.helpers.destinationOrTerritorialValidity"),
      width: 230,
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <Ellipsis>
          {record.destination
            ? t("calc.travel.enums.destination." + record.destination)
            : t("calc.travel.enums.territorialValidity." + record.territorialValidity)}
        </Ellipsis>
      ),
    },
    {
      key: "riskGroup",
      title: t("calc.travel.enums.riskGroup._label"),
      width: 170,
      ellipsis: { showTitle: false },
      render: (_, record) =>
        record.riskGroup && <Ellipsis>{t("calc.travel.enums.riskGroup." + record.riskGroup)}</Ellipsis>,
    },
    {
      key: "insuredClientsCount",
      title: t("calc.travel.attrs.clientsData.insuredClientsCount"),
      width: 60,
      align: "right",
      render: (_, record) => record.insuredClientsCount,
    },
    {
      key: "attachmentsCount",
      title: t("calc.draft.attrs.attachmentsCount"),
      width: 60,
      align: "right",
      render: (_, record) => record.attachments?.length || 0,
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 190,
      render: (_, record) => (
        <>
          <ActionTextIcon
            path={`${CALC_ROUTE_PATHS.travel.to}${URLQuery.searchParamsToString({ searchParams: { [QueryKeys.DRAFT_ID]: record.id } })}`}
            target="_blank"
            icon="import"
            color="blue"
            text={t("calc.draft.actions.setToCalcForm")}
          />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("calc.draft.titles.deleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            okText={t("common.yes")}
            cancelText={t("common.no")}
            okType="danger"
            onConfirm={() => deleteCalcDraft.mutate(record.id)}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Table<TravelCalcDraftList>
      {...tableStandardProps()}
      columns={columns}
      scroll={{ x: TableSizes.LARGE }}
      dataSource={data?.pageData as TravelCalcDraftList[] | undefined}
      expandable={{
        expandedRowRender: (record) => <CalcDraftAttachmentsTableView calcDraft={record} />,
      }}
      pagination={{
        ...paginationTableProps,
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
        onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
      }}
    />
  );
};
